import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Snackbar, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { authActions } from '../store/auth-slice';

const SessionTimeoutWarning = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [loginTimestamp, setLoginTimestamp] = useState(null);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLogin = useSelector((state) => state.auth.isLogin);
  
  const SESSION_DURATION = 60 * 60 * 1000; // 60 minutes in milliseconds
  const WARNING_TIMES = [10 * 60 * 1000, 5 * 60 * 1000, 1 * 60 * 1000]; // 10, 5, 1 minutes
  const [warningsShown, setWarningsShown] = useState(new Set());

  // Initialize login timestamp when user logs in
  useEffect(() => {
    if (isLogin && !loginTimestamp) {
      const timestamp = Date.now();
      setLoginTimestamp(timestamp);
      localStorage.setItem('loginTimestamp', timestamp.toString());
      setWarningsShown(new Set());
    } else if (!isLogin) {
      setLoginTimestamp(null);
      localStorage.removeItem('loginTimestamp');
      setWarningsShown(new Set());
    }
  }, [isLogin]);

  // Load login timestamp from localStorage on mount
  useEffect(() => {
    const storedTimestamp = localStorage.getItem('loginTimestamp');
    if (storedTimestamp && isLogin) {
      setLoginTimestamp(parseInt(storedTimestamp));
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/company-master/logout`, {
        credentials: 'include',
      });
      dispatch(authActions.setLogout());
      localStorage.removeItem('activeUserName');
      localStorage.removeItem('companyLogoURL');
      localStorage.removeItem('loginTimestamp');
      localStorage.removeItem('companyLogoBase64');
      setShowDialog(false);
      navigate('/');
    } catch (err) {
      console.error('Logout error:', err);
    }
  }, [dispatch, navigate]);

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!isLogin || !loginTimestamp) return;

    const checkSessionTimeout = () => {
      const currentTime = Date.now();
      const elapsedTime = currentTime - loginTimestamp;
      const remaining = SESSION_DURATION - elapsedTime;

      setTimeRemaining(remaining);

      // Session expired
      if (remaining <= 0) {
        setShowDialog(false);
        setSnackbarMessage('Your session has expired. Please login again.');
        setSnackbarOpen(true);
        setTimeout(() => {
          handleLogout();
        }, 2000);
        return;
      }

      // Check for warning times
      WARNING_TIMES.forEach((warningTime) => {
        const warningKey = warningTime.toString();
        
        // Show warning if we're within the warning window and haven't shown it yet
        if (remaining <= warningTime && remaining > warningTime - 5000 && !warningsShown.has(warningKey)) {
          const minutes = Math.floor(warningTime / 60000);
          
          if (minutes === 1) {
            // Show dialog for 1 minute warning
            setShowDialog(true);
          } else {
            // Show snackbar for 10 and 5 minute warnings
            setSnackbarMessage(`Your session will expire in ${minutes} minutes. Please save your work.`);
            setSnackbarOpen(true);
          }
          
          setWarningsShown(prev => new Set([...prev, warningKey]));
        }
      });
    };

    // Check immediately
    checkSessionTimeout();

    // Check every second
    const interval = setInterval(checkSessionTimeout, 1000);

    return () => clearInterval(interval);
  }, [isLogin, loginTimestamp, warningsShown, handleLogout]);



  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleResume = useCallback(() => {
    // Extend the session locally by resetting the login timestamp
    const now = Date.now();
    setLoginTimestamp(now);
    localStorage.setItem('loginTimestamp', now.toString());
    setShowDialog(false);
    setSnackbarMessage('Session resumed');
    setSnackbarOpen(true);
  }, [setLoginTimestamp, setShowDialog, setSnackbarMessage, setSnackbarOpen]);

  if (!isLogin) return null;

  return (
    <>
      {/* Critical Warning Dialog (1 minute) */}
      <Dialog
        open={showDialog}
        onClose={() => {}} // Prevent closing by clicking outside
        aria-labelledby="session-timeout-dialog"
        disableEscapeKeyDown
      >
        <DialogTitle id="session-timeout-dialog" sx={{ color: 'error.main' }}>
          ⚠️ Session Expiring Soon!
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your session will expire in {timeRemaining ? formatTime(timeRemaining) : '1:00'}.
            <br />
            <br />
            Please save your work. You will be automatically logged out when the session expires.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogout} variant="contained" color="error">
            Logout Now
          </Button>
          <Button onClick={handleResume} variant="contained" color="primary" sx={{ ml: 1 }}>
            Resume Session
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for 10 and 5 minute warnings */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SessionTimeoutWarning;